/**
 * Money plan: a personal general ledger and cash-flow forecast.
 *
 * Everything here is pure and shared by the API (which computes the overview and
 * guards card spending) and the app. Amounts are integer minor units; conversions
 * between currencies use indicative rates and are labelled as such in the UI.
 */
import { CURRENCIES, type Currency } from "./money";

/* ───────── Accounts ───────── */

export const ACCOUNT_KINDS = ["chequing", "savings", "mobile_money", "cash", "credit_card", "loan", "investment", "other"] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
/** Money you can spend this week. */
export const LIQUID_KINDS: readonly AccountKind[] = ["chequing", "savings", "mobile_money", "cash"];
/** Balances you owe (entered as a positive amount owed). */
export const LIABILITY_KINDS: readonly AccountKind[] = ["credit_card", "loan"];

/* ───────── Scheduled flows (bills, income, debts…) ───────── */

export const SCHEDULE_KINDS = ["income", "bill", "debt", "savings", "remittance"] as const;
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number];
export const SCHEDULE_FREQUENCIES = ["once", "weekly", "biweekly", "monthly", "quarterly", "yearly"] as const;
export type ScheduleFrequency = (typeof SCHEDULE_FREQUENCIES)[number];

export const PLAN_CATEGORIES = [
  "housing",
  "utilities",
  "food",
  "transport",
  "health",
  "insurance",
  "education",
  "family",
  "remittance",
  "debt",
  "savings",
  "income",
  "circle",
  "giving",
  "shopping",
  "leisure",
  "fees",
  "other",
] as const;
export type PlanCategory = (typeof PLAN_CATEGORIES)[number];

/* ───────── Indicative FX ───────── */

/**
 * Units of each currency per 1 US dollar, indicative only (October 2026). XAF and
 * XOF are pegged to the euro at exactly 655.957, so they move with EUR.
 */
export const FX_AS_OF = "2026-10-20";
const EUR_PER_USD = 0.92;
export const FX_PER_USD: Record<Currency, number> = {
  USD: 1,
  CAD: 1.38,
  EUR: EUR_PER_USD,
  XAF: EUR_PER_USD * 655.957,
  XOF: EUR_PER_USD * 655.957,
  NGN: 1550,
  GHS: 15.5,
  KES: 129,
};

/** Convert minor units between currencies at indicative rates (rounded to the target's minor unit). */
export function convertMinor(minor: number, from: Currency, to: Currency): number {
  if (from === to) return minor;
  const major = minor / 10 ** CURRENCIES[from].minor;
  const target = (major / FX_PER_USD[from]) * FX_PER_USD[to];
  return Math.round(target * 10 ** CURRENCIES[to].minor);
}

/**
 * Convert an amount someone must pay, rounding up to the next minor unit (never below 1), so
 * a conversion never charges less than the purchase is worth.
 */
export function convertMinorUp(minor: number, from: Currency, to: Currency): number {
  if (from === to) return minor;
  const major = minor / 10 ** CURRENCIES[from].minor;
  const target = (major / FX_PER_USD[from]) * FX_PER_USD[to] * 10 ** CURRENCIES[to].minor;
  return Math.max(1, Math.ceil(target - 1e-9));
}

/* ───────── Dates ───────── */

export const addDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
export const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/** The nth occurrence after an anchor date (month ends are clamped: Jan 31 → Feb 28). */
export function nthOccurrence(anchor: string, frequency: ScheduleFrequency, n: number): string {
  if (frequency === "once") return anchor;
  if (frequency === "weekly") return addDays(anchor, 7 * n);
  if (frequency === "biweekly") return addDays(anchor, 14 * n);
  const months = frequency === "monthly" ? n : frequency === "quarterly" ? 3 * n : 12 * n;
  const [y, m, d] = anchor.split("-").map(Number) as [number, number, number];
  const lastDay = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(d, lastDay))).toISOString().slice(0, 10);
}

/**
 * Every occurrence of a schedule from its next due date up to `until` (inclusive). When `today`
 * is given, occurrences already in the past count once (an unmarked bill is one overdue bill,
 * not one per missed week since it was entered).
 */
export function occurrences(nextDue: string, frequency: ScheduleFrequency, until: string, today?: string, max = 400): string[] {
  const out: string[] = [];
  let overdueSeen = false;
  for (let n = 0; n < 100_000 && out.length < max; n++) {
    const d = nthOccurrence(nextDue, frequency, n);
    if (d > until) break;
    if (today && d < today) {
      if (!overdueSeen) out.push(d);
      overdueSeen = true;
    } else out.push(d);
    if (frequency === "once") break;
  }
  return out;
}

/** The next due date after `done` occurrences were marked paid, counted from the original anchor (no month-end drift). */
export function advanceFromAnchor(anchor: string, current: string, frequency: ScheduleFrequency): string {
  if (frequency === "once") return current;
  for (let n = 1; n < 10_000; n++) {
    const d = nthOccurrence(anchor, frequency, n);
    if (d > current) return d;
  }
  return nthOccurrence(current, frequency, 1);
}

/** Average monthly amount of a recurring flow (one-off flows count as 0). */
export function monthlyEquivalent(amountMinor: number, frequency: ScheduleFrequency): number {
  switch (frequency) {
    case "weekly":
      return Math.round((amountMinor * 52) / 12);
    case "biweekly":
      return Math.round((amountMinor * 26) / 12);
    case "monthly":
      return amountMinor;
    case "quarterly":
      return Math.round(amountMinor / 3);
    case "yearly":
      return Math.round(amountMinor / 12);
    default:
      return 0;
  }
}

/* ───────── Forecast and financial power ───────── */

export interface Flow {
  /** ISO date; dates before today are overdue and fall due today. */
  date: string;
  /** Signed, in the base currency: + money in, − money out. */
  amountMinor: number;
  label: string;
  kind: ScheduleKind | "circle";
  category: string;
  sourceId: string;
}

export interface ForecastInput {
  today: string;
  horizonDays: number;
  liquidMinor: number;
  flows: Flow[];
  /** Expected everyday spending not covered by a schedule (groceries, transport…). */
  dailySpendMinor: number;
  /** Cushion the plan tries never to go below. */
  bufferMinor: number;
}

export interface ForecastPoint {
  date: string;
  balanceMinor: number;
  inMinor: number;
  outMinor: number;
}

export interface ForecastResult {
  points: ForecastPoint[];
  lowest: { date: string; balanceMinor: number };
  /** First day the projected balance goes below zero, if any. */
  shortfallDate: string | null;
  /** First day it goes below the safety buffer, if any. */
  belowBufferDate: string | null;
  nextIncome: { date: string; amountMinor: number } | null;
  /** Obligations from today until the next income (or 30 days), overdue included. */
  committedUntilIncomeMinor: number;
  /** What can be spent per day until the next income without endangering anything planned. */
  safeToSpendTodayMinor: number;
  endBalanceMinor: number;
}

export function forecast(input: ForecastInput): ForecastResult {
  const { today, horizonDays, flows, dailySpendMinor, bufferMinor } = input;
  const end = addDays(today, horizonDays);
  const byDay = new Map<string, Flow[]>();
  for (const f of flows) {
    const day = f.date < today ? today : f.date;
    if (day > end) continue;
    byDay.set(day, [...(byDay.get(day) ?? []), f]);
  }

  const run = (spend: number) => {
    let bal = input.liquidMinor;
    const points: ForecastPoint[] = [];
    for (let i = 0; i <= horizonDays; i++) {
      const date = addDays(today, i);
      let inM = 0;
      let outM = spend;
      for (const f of byDay.get(date) ?? []) {
        if (f.amountMinor >= 0) inM += f.amountMinor;
        else outM += -f.amountMinor;
      }
      bal += inM - outM;
      points.push({ date, balanceMinor: bal, inMinor: inM, outMinor: outM });
    }
    return points;
  };

  const points = run(dailySpendMinor);
  const lowestPoint = points.reduce((lo, p) => (p.balanceMinor < lo.balanceMinor ? p : lo), points[0]!);
  const shortfall = points.find((p) => p.balanceMinor < 0)?.date ?? null;
  const belowBuffer = points.find((p) => p.balanceMinor < bufferMinor)?.date ?? null;

  const income = flows.filter((f) => f.amountMinor > 0 && f.date > today).sort((a, b) => (a.date < b.date ? -1 : 1));
  const nextIncome = income[0] ? { date: income[0].date, amountMinor: income.filter((f) => f.date === income[0]!.date).reduce((s, f) => s + f.amountMinor, 0) } : null;
  const until = nextIncome?.date ?? addDays(today, 30);
  const committed = flows.filter((f) => f.amountMinor < 0 && (f.date < until || f.date < today)).reduce((s, f) => s - f.amountMinor, 0);
  const days = Math.max(1, daysBetween(today, until));

  // Allowance until payday: what's left after everything due before then, spread over the days.
  const allowance = (input.liquidMinor - committed - bufferMinor) / days;
  // Capped so later obligations in the horizon stay covered: spending s a day until payday
  // lowers every later balance by s × days, so it must fit inside the lowest point (no spend).
  const lowestNoSpend = run(0).reduce((lo, p) => Math.min(lo, p.balanceMinor), Infinity);
  const cap = (lowestNoSpend - bufferMinor) / days;
  const safe = Math.max(0, Math.floor(Math.min(allowance, cap)));

  return {
    points,
    lowest: { date: lowestPoint.date, balanceMinor: lowestPoint.balanceMinor },
    shortfallDate: shortfall,
    belowBufferDate: belowBuffer,
    nextIncome,
    committedUntilIncomeMinor: committed,
    safeToSpendTodayMinor: safe,
    endBalanceMinor: points.at(-1)!.balanceMinor,
  };
}

export type HealthStatus = "safe" | "watch" | "at_risk";
export type HealthReason =
  | "shortfall"
  | "below_buffer"
  | "high_debt_service"
  | "elevated_debt_service"
  | "short_runway"
  | "thin_runway"
  | "spending_exceeds_income"
  | "overdue"
  | "no_income";

export interface PowerInput {
  liquidMinor: number;
  investmentsMinor: number;
  liabilitiesMinor: number;
  monthlyIncomeMinor: number;
  monthlyCommittedMinor: number;
  /** Housing + debt repayments, for the debt-service ratio. */
  monthlyDebtServiceMinor: number;
  dailySpendMinor: number;
  overdueCount: number;
  forecast: ForecastResult;
  bufferMinor: number;
}

export interface PowerResult {
  status: HealthStatus;
  reasons: HealthReason[];
  netWorthMinor: number;
  /** Months the liquid money lasts with no income, at the planned outflow. */
  runwayMonths: number | null;
  /** (Housing + debt payments) / income, as a ratio (0.4 = 40%). Canadian lenders cap total debt service near 44%. */
  debtServiceRatio: number | null;
  monthlySurplusMinor: number;
}

export function financialPower(p: PowerInput): PowerResult {
  const monthlyOut = p.monthlyCommittedMinor + p.dailySpendMinor * 30;
  const runway = monthlyOut > 0 ? p.liquidMinor / monthlyOut : null;
  const dsr = p.monthlyIncomeMinor > 0 ? p.monthlyDebtServiceMinor / p.monthlyIncomeMinor : null;
  const surplus = p.monthlyIncomeMinor - monthlyOut;
  const reasons: HealthReason[] = [];
  let status: HealthStatus = "safe";
  const worse = (s: HealthStatus) => {
    if (s === "at_risk" || (s === "watch" && status === "safe")) status = s;
  };
  if (p.forecast.shortfallDate) {
    reasons.push("shortfall");
    worse("at_risk");
  } else if (p.forecast.belowBufferDate && p.bufferMinor > 0) {
    reasons.push("below_buffer");
    worse("watch");
  }
  if (p.overdueCount > 0) {
    reasons.push("overdue");
    worse("watch");
  }
  if (dsr !== null && dsr > 0.44) {
    reasons.push("high_debt_service");
    worse("at_risk");
  } else if (dsr !== null && dsr > 0.36) {
    reasons.push("elevated_debt_service");
    worse("watch");
  }
  if (p.monthlyIncomeMinor === 0 && monthlyOut > 0) {
    reasons.push("no_income");
    worse("watch");
  } else if (surplus < 0) {
    reasons.push("spending_exceeds_income");
    worse("watch");
  }
  if (runway !== null && runway < 1) {
    reasons.push("short_runway");
    worse(surplus < 0 ? "at_risk" : "watch");
  } else if (runway !== null && runway < 3) {
    reasons.push("thin_runway");
  }
  return {
    status,
    reasons,
    netWorthMinor: p.liquidMinor + p.investmentsMinor - p.liabilitiesMinor,
    runwayMonths: runway === null ? null : Math.round(runway * 10) / 10,
    debtServiceRatio: dsr === null ? null : Math.round(dsr * 1000) / 1000,
    monthlySurplusMinor: surplus,
  };
}
